import { FeatureFlagKv, FeatureFlagStatus } from '__generated__/graphql';
import React, { createContext, ReactNode, useContext } from 'react';
import { useAuthContext } from './auth.context';
import { useGetAllTenantFeatureFlags } from 'hooks/tenants/queries/useGetAllTenantFeatureFlags';
import { ApolloError } from '@apollo/client';

interface FeatureFlagContextInterface {
  featureFlags: FeatureFlagKv[];
  featureFlagsLoading: boolean;
  featureFlagsError: ApolloError | null;
  refetchFeatureFlags: () => void;
  checkFeatureFlagIsEnabled: (featureFlag: string) => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextInterface>({
  featureFlags: [],
  featureFlagsLoading: false,
  featureFlagsError: null,
  refetchFeatureFlags: () => {},
  checkFeatureFlagIsEnabled: () => false,
});

export const FeatureFlagContextProvider = ({ children }: { children: ReactNode }) => {
  const { userTenantId } = useAuthContext();
  const { data, loading, error, refetch } = useGetAllTenantFeatureFlags(userTenantId ?? 0);
  const featureFlags: FeatureFlagKv[] = data?.getFeatureFlags?.featureFlags ?? [];

  const checkFeatureFlagIsEnabled = (featureFlag: string) => {
    return (
      featureFlags.find(({ key }) => key === featureFlag)?.value?.status ===
      FeatureFlagStatus.Active
    );
  };

  return (
    <FeatureFlagContext.Provider
      value={{
        featureFlags,
        featureFlagsLoading: loading,
        featureFlagsError: error || null,
        refetchFeatureFlags: refetch,
        checkFeatureFlagIsEnabled,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagContext = () => useContext(FeatureFlagContext);
